/**
 * Contratos de administracion de estudiantes y su asignacion academica.
 *
 * El codigo lo escribe una persona (no se autogenera) y es unico por institucion
 * SIN distinguir mayusculas de minusculas. La asignacion academica vive en su propia
 * entidad porque un estudiante tiene una distinta por ciclo — y puede no tener
 * ninguna en un ciclo futuro y aun asi recibir cargos y pagos.
 */

import { z } from "zod";
import { idSchema, instantSchema, operationalDateSchema } from "./common.js";

/**
 * Codigo de estudiante. Se admite lo que una secretaria realmente teclea:
 * letras, numeros, guiones y puntos. Se normaliza a mayusculas para la unicidad.
 */
export const studentCodeSchema = z
  .string()
  .trim()
  .min(1, "El código es obligatorio")
  .max(30, "El código no puede exceder 30 caracteres")
  .regex(
    /^[A-Za-z0-9][A-Za-z0-9._-]*$/,
    "El código solo admite letras, números, punto, guion y guion bajo",
  );

/**
 * Telefono del responsable. NO se asume una longitud fija: los numeros
 * internacionales varian entre 7 y 15 digitos, y aqui se admite el prefijo `+`
 * y separadores de lectura.
 */
export const phoneSchema = z
  .string()
  .trim()
  .min(7, "El teléfono es demasiado corto")
  .max(25, "El teléfono es demasiado largo")
  .regex(
    /^\+?[0-9][0-9\s()-]*$/,
    "El teléfono solo admite dígitos, espacios, guiones, paréntesis y un + inicial",
  )
  .refine((value) => (value.match(/\d/g) ?? []).length >= 7, {
    message: "El teléfono debe tener al menos 7 dígitos",
  });

export const studentAssignmentSchema = z.object({
  cycleId: idSchema,
  cycleName: z.string(),
  cycleYear: z.number().int(),
  gradeId: idSchema.nullable(),
  gradeName: z.string().nullable(),
  sectionId: idSchema.nullable(),
  sectionName: z.string().nullable(),
  status: z.string(),
  assignedAt: instantSchema,
  /**
   * Versión de la ASIGNACIÓN, no la del estudiante.
   *
   * Es la que espera `changeAssignment`. Sin exponerla, el cliente solo podía
   * mandar 0 —que el servidor toma como «no compruebes»— y dos personas moviendo
   * al mismo alumno de aula a la vez se pisaban en silencio.
   */
  version: z.number().int(),
});
export type StudentAssignmentDto = z.infer<typeof studentAssignmentSchema>;

export const studentAdminSchema = z.object({
  id: idSchema,
  institutionId: idSchema,
  code: z.string(),
  firstNames: z.string().nullable(),
  lastNames: z.string().nullable(),
  fullName: z.string(),
  birthDate: operationalDateSchema.nullable(),
  guardianName: z.string().nullable(),
  guardianPhone: z.string().nullable(),
  active: z.boolean(),
  assignments: z.array(studentAssignmentSchema),
  createdAt: instantSchema,
  updatedAt: instantSchema,
  version: z.number().int(),
});
export type StudentAdminDto = z.infer<typeof studentAdminSchema>;

const assignmentInputSchema = z.object({
  cycleId: idSchema,
  gradeId: idSchema,
  sectionId: idSchema,
});

export const createStudentRequestSchema = z.object({
  /** Identificador global generado en el dispositivo ANTES de guardar (permite offline). */
  studentId: idSchema,
  code: studentCodeSchema,
  firstNames: z.string().trim().min(2, "Los nombres son obligatorios").max(80),
  lastNames: z.string().trim().min(2, "Los apellidos son obligatorios").max(80),
  birthDate: operationalDateSchema,
  guardianName: z
    .string()
    .trim()
    .min(3, "El responsable es obligatorio")
    .max(120),
  guardianPhone: phoneSchema,
  /**
   * Asignacion inicial. Es opcional: un estudiante puede darse de alta en un ciclo
   * futuro antes de que existan grados y secciones asignados.
   */
  assignment: assignmentInputSchema.nullable().optional(),
  /** Ciclo cuyos cargos vigentes se le asignaran al darlo de alta. */
  cycleId: idSchema,
});
export type CreateStudentRequest = z.infer<typeof createStudentRequestSchema>;

export const updateStudentRequestSchema = z.object({
  code: studentCodeSchema.optional(),
  firstNames: z.string().trim().min(2).max(80).optional(),
  lastNames: z.string().trim().min(2).max(80).optional(),
  birthDate: operationalDateSchema.optional(),
  guardianName: z.string().trim().min(3).max(120).optional(),
  guardianPhone: phoneSchema.optional(),
  expectedVersion: z.number().int().positive(),
});
export type UpdateStudentRequest = z.infer<typeof updateStudentRequestSchema>;

export const changeAssignmentRequestSchema = assignmentInputSchema.and(
  z.object({ expectedVersion: z.number().int().nonnegative() }),
);
export type ChangeAssignmentRequest = z.infer<
  typeof changeAssignmentRequestSchema
>;

export const setStudentActiveRequestSchema = z.object({
  active: z.boolean(),
  reason: z.string().trim().max(300).nullable().optional(),
  expectedVersion: z.number().int().positive(),
});
export type SetStudentActiveRequest = z.infer<
  typeof setStudentActiveRequestSchema
>;

// ---------------------------------------------------------------------------
// Cargos que hereda un estudiante nuevo
// ---------------------------------------------------------------------------

export const inheritedChargeSchema = z.object({
  batchId: idSchema,
  conceptId: idSchema,
  conceptName: z.string(),
  month: z.number().int().min(1).max(12).nullable(),
  monthLabel: z.string().nullable(),
  amountMinor: z.number().int(),
  dueDate: operationalDateSchema.nullable(),
  /** Mora ya acumulada si el cargo heredado viene vencido. */
  lateFeeMinor: z.number().int(),
});
export type InheritedChargeDto = z.infer<typeof inheritedChargeSchema>;

export const inheritedChargesPreviewSchema = z.object({
  cycleId: idSchema,
  cycleName: z.string(),
  currency: z.string(),
  asOf: operationalDateSchema,
  charges: z.array(inheritedChargeSchema),
  totalCapitalMinor: z.number().int(),
  totalLateFeeMinor: z.number().int(),
  notice: z.string(),
});
export type InheritedChargesPreviewDto = z.infer<
  typeof inheritedChargesPreviewSchema
>;

export const createStudentResultSchema = z.object({
  student: studentAdminSchema,
  inheritedCharges: z.number().int(),
  inheritedCapitalMinor: z.number().int(),
  currency: z.string(),
  alreadyExisted: z.boolean(),
});
export type CreateStudentResultDto = z.infer<typeof createStudentResultSchema>;

export const studentAdminListQuerySchema = z.object({
  search: z.string().trim().max(80).optional(),
  cycleId: idSchema.optional(),
  gradeId: idSchema.optional(),
  sectionId: idSchema.optional(),
  estado: z.enum(["ACTIVOS", "INACTIVOS", "TODOS"]).default("ACTIVOS"),
});
export type StudentAdminListQuery = z.infer<typeof studentAdminListQuerySchema>;

// ---------------------------------------------------------------------------
// Catalogos academicos: ciclos, grados y secciones
// ---------------------------------------------------------------------------

export const createCycleRequestSchema = z
  .object({
    name: z
      .string()
      .trim()
      .min(3, "El nombre del ciclo es obligatorio")
      .max(80),
    year: z.number().int().min(2000).max(2999),
    startDate: operationalDateSchema,
    endDate: operationalDateSchema,
  })
  .refine((value) => value.endDate >= value.startDate, {
    message: "La fecha de finalización no puede ser anterior a la de inicio",
    path: ["endDate"],
  });
export type CreateCycleRequest = z.infer<typeof createCycleRequestSchema>;

export const updateCycleRequestSchema = z.object({
  name: z.string().trim().min(3).max(80).optional(),
  startDate: operationalDateSchema.optional(),
  endDate: operationalDateSchema.optional(),
  /** Cerrar un ciclo impide generar cargos y registrar pagos nuevos en él. */
  closed: z.boolean().optional(),
  /** Marca el ciclo vigente. Solo uno puede serlo a la vez. */
  isCurrent: z.boolean().optional(),
  expectedVersion: z.number().int().positive(),
});
export type UpdateCycleRequest = z.infer<typeof updateCycleRequestSchema>;

export const createGradeRequestSchema = z.object({
  name: z.string().trim().min(2, "El nombre del grado es obligatorio").max(80),
  /** Orden de presentación. Se usa para listar los grados como los ve la institución. */
  orderIndex: z.number().int().min(0).max(999).default(0),
});
export type CreateGradeRequest = z.infer<typeof createGradeRequestSchema>;

export const createSectionRequestSchema = z.object({
  gradeId: idSchema,
  name: z
    .string()
    .trim()
    .min(1, "El nombre de la sección es obligatorio")
    .max(40),
});
export type CreateSectionRequest = z.infer<typeof createSectionRequestSchema>;

export const renameRequestSchema = z.object({
  name: z.string().trim().min(1).max(80),
  expectedVersion: z.number().int().positive(),
});
export type RenameRequest = z.infer<typeof renameRequestSchema>;
